/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  FilePdfOutlined,
  HistoryOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  PlusOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { Popover, Row } from 'antd';
import { ScheduleOfValues } from 'ontraccr-common';

import moment from 'moment';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import ProgressRow from './ProgressRow';
import { percentageFormatter } from '../../helpers/helpers';
import ScheduleOfValuesInputField from './ScheduleOfValuesInputField';
import { currencyFormatter } from '../../helpers/inputParsers';

import { getNumberText } from './helpers';

import colors from '../../constants/Colors';

const getScheduleOfValuesInputField = ({
  record = {},
  readOnly,
  field,
  title,
  text,
  additionalProps = {},
} = {}) => (
  !readOnly
    ? (
      <ScheduleOfValuesInputField
        {...record}
        field={field}
        type="number"
        formatter={currencyFormatter}
        title={title}
        min={Number.MIN_SAFE_INTEGER}
        {...additionalProps}
      />
    ) : <div>{currencyFormatter(Number(text).toFixed(2))}</div>
);

const getLiveSummaryColumns = ({
  readOnly = false,
  onClick,
  showHoldback = false,
  isScheduleOfValueLocked = false,
  isPastBill = false,
  onHoldbackInvoiceClicked,
}) => {
  const columns = [
    {
      title: 'Contract Total',
      dataIndex: 'totalContractValue',
      width: 120,
      render: (text) => (
        <div>{currencyFormatter(Number(text).toFixed(2))}</div>
      ),
    },
    {
      title: 'Total Base Contract',
      dataIndex: 'totalContractAmount',
      width: 140,
      render: (text, record) => (
        getScheduleOfValuesInputField({
          readOnly: readOnly || isScheduleOfValueLocked || isPastBill,
          record,
          text,
          field: 'totalContractAmount',
          title: 'Total Base Contract Value',
        })
      ),
    },
    {
      title: 'Total Changes',
      dataIndex: 'totalChanges',
      width: 120,
      render: (text) => (
        <div>{currencyFormatter(Number(text).toFixed(2))}</div>
      ),
    },
  ];

  if (showHoldback) {
    columns.push(
      {
        title: 'Total Non-Holdback Items',
        dataIndex: 'totalNonHoldbackAmount',
        width: 120,
        render: (text) => (
          <div>{currencyFormatter(Number(text).toFixed(2))}</div>
        ),
      },
    );
  }

  columns.push(
    {
      title: 'Total Billed To Date',
      dataIndex: 'totalBilledToDate',
      width: 120,
      render: (text) => (
        <div>{currencyFormatter(Number(text).toFixed(2))}</div>
      ),
    },
    {
      title: 'Current Base Contract',
      dataIndex: 'currentContractValue',
      width: 120,
      render: (text) => (
        <div>{currencyFormatter(Number(text).toFixed(2))}</div>
      ),
    },
    {
      title: 'Invoice Amount',
      dataIndex: 'invoiceAmount',
      width: 120,
      render: (text) => (
        <div>{currencyFormatter(Number(text).toFixed(2))}</div>
      ),
    },
  );

  if (showHoldback) {
    columns.push(
      {
        title: 'Total Holdback to Date',
        dataIndex: 'holdbackAmount',
        width: 200,
        render: (text) => (
          <Row justify="space-between" align="middle">
            <div style={{ paddingRight: 10 }}>{currencyFormatter(Number(text).toFixed(2))}</div>
            { onHoldbackInvoiceClicked && Number(text) !== 0 && (
              <OnTraccrButton
                title="Invoice"
                onClick={onHoldbackInvoiceClicked}
              />
            )}
          </Row>
        ),
      },
      {
        title: 'Holdback Percentage',
        dataIndex: 'holdbackPercentage',
        width: 120,
        render: (text, record) => (
          (!readOnly && !isScheduleOfValueLocked && !isPastBill)
            ? (
              <ScheduleOfValuesInputField
                {...record}
                field="holdbackPercentage"
                type="number"
                subType="percentage"
                formatter={(val) => `${val} %`}
                title="Holdback Percentage"
                min={0}
                max={100}
                precision={2}
              />
            ) : <div>{percentageFormatter(text)}</div>
        ),
      },
    );
  }

  if (!isPastBill && !readOnly) {
    columns.push({
      title: '',
      dataIndex: 'submit',
      align: 'right',
      width: 40,
      render: () => (
        <OnTraccrButton
          title="Submit"
          onClick={onClick}
        />
      ),
    });
  }

  return columns;
};

const getLogColumns = ({
  onPdf,
  onEdit,
  onHistory,
  onRevertHoldback,
}) => [
  {
    title: 'Date',
    dataIndex: 'timestamp',
    width: 120,
    render: (text) => (
      <div>{moment(text).format('MM/DD/YYYY hh:mm A')}</div>
    ),
  },
  {
    title: 'Invoice Amount',
    dataIndex: 'invoiceAmount',
    width: 120,
    render: (text) => (
      <div>{currencyFormatter(Number(text).toFixed(2))}</div>
    ),
  },
  {
    title: 'Holdback Amount',
    dataIndex: 'holdbackAmount',
    width: 120,
    render: (text, record) => (
      <div>
        {record.isHoldbackInvoiceReverted
          ? currencyFormatter(0)
          : currencyFormatter(Number(text || 0).toFixed(2))}
      </div>
    ),
  },
  {
    title: 'Notes',
    dataIndex: 'notes',
    width: 300,
    render: (text) => (
      <div>{text}</div>
    ),
  },
  {
    dataIndex: 'history',
    width: 20,
    render: (_, record) => (
      <BorderlessButton
        style={{ padding: 0, width: 10 }}
        iconNode={<HistoryOutlined style={{ marginLeft: 0 }} />}
        onClick={() => onHistory(record)}
      />
    ),
  },
  {
    dataIndex: 'pdf',
    width: 20,
    render: (_, record) => (
      <BorderlessButton
        style={{ padding: 0, width: 10 }}
        iconNode={<FilePdfOutlined style={{ marginLeft: 0 }} />}
        onClick={() => onPdf(record)}
      />
    ),
  },
  {
    dataIndex: 'edit',
    width: 20,
    render: (_, record) => (
      <BorderlessButton
        style={{ padding: 0, width: 10 }}
        iconNode={<EyeOutlined style={{ marginLeft: 0 }} />}
        onClick={() => onEdit(record)}
      />
    ),
  },
  {
    dataIndex: 'revertHoldback',
    width: 150,
    render: (_, record) => {
      if (
        record.isHoldbackInvoice
        || Number(record.holdbackAmount || 0) <= 0
        || record.isHoldbackInvoiceReverted
      ) return null;

      return (
        <OnTraccrButton
          iconLeft
          icon={<UndoOutlined style={{ marginLeft: 0 }} />}
          onClick={() => onRevertHoldback(record)}
          title="Revert Holdback"
          type="other"
        />
      )
    },
  },
];

const getScheduleOfValueColumns = ({
  onHistory,
  onDelete,
  onInfo,
  onNotesSelect,
  onAddSubItem,
  readOnly = false,
  isSubContract = false,
  liveSummaryValues = [],
  isScheduleOfValueLocked = false,
  hideButtons = false,
  isProgress = false,
  isChanges = false,
  isPastBill = false,
  visibleColumns = [],
}) => {
  const visibleColumnsSet = new Set(visibleColumns);
  const cols = [
    {
      title: 'Item #',
      dataIndex: 'itemNumber',
      width: 10,
      render: (_, record) => (
        <div style={{
          textAlign: 'left',
          width: '100%',
        }}
        >
          {getNumberText(record)}
        </div>
      ),
    },
    ...isChanges
      ? [{
        title: 'Number',
        dataIndex: 'number',
        width: 100,
        render: (number, record) => (
          !readOnly && !isScheduleOfValueLocked
            ? (
              <ScheduleOfValuesInputField
                {...record}
                field="number"
                type="text"
                title="Number"
                placeholder=""
              />
            ) : <div>{number}</div>
        ),
      }] : [],
    {
      title: 'Description',
      dataIndex: 'description',
      width: 200,
      render: (text, record) => {
        // Description stays editable on rollup parents, it is only their numbers that are derived.
        const className = record.hasSubItems ? 'sov-parent-description' : undefined;
        const popoverContent = (!readOnly && !isScheduleOfValueLocked && !isPastBill)
          ? (
            <div className={className}>
              <ScheduleOfValuesInputField
                {...record}
                field="description"
                type="text"
                title="Description"
                isProgress={isProgress}
              />
            </div>
          ) : <div className={className} style={{ width: '100%', maxWidth: '200px' }}>{text}</div>;
        return (
          text ? (
            <Popover
              placement="left"
              content={<div style={{ width: 250 }}>{text}</div>}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            >
              {popoverContent}
            </Popover>
          ) : (
            <div>{popoverContent}</div>
          )
        );
      },
    },
    ...(isProgress && isSubContract)
      ? [{
        title: 'Contract Total',
        dataIndex: 'contractTotal',
        width: 140,
        render: (text) => (
          <div>{currencyFormatter(Number(text).toFixed(2))}</div>
        ),
      }] : [],
    {
      title: 'Contract Amount',
      dataIndex: 'contractAmount',
      width: 140,
      render: (text, record) => (
        getScheduleOfValuesInputField({
          readOnly: readOnly || isScheduleOfValueLocked || isPastBill || record.hasSubItems,
          record,
          text,
          field: 'contractAmount',
          title: 'Contract Amount',
          additionalProps: { liveSummaryValues, isProgress },
        })
      ),
    },
    ...isSubContract
      ? [{
        title: 'Total Changes',
        dataIndex: 'totalChanges',
        width: 100,
        render: (text) => (
          <div>{currencyFormatter(Number(text).toFixed(2))}</div>
        ),
      }]
      : [],
    {
      title: '% Complete',
      dataIndex: 'percentComplete',
      width: 140,
      render: (_, record) => (
        <ProgressRow
          {...record}
          readOnly={readOnly || !!record.hasSubItems}
          isProgress={isProgress}
        />
      ),
    },
    {
      title: 'Progress To Date',
      dataIndex: 'progressToDate',
      width: 100,
      render: (text) => (
        <div>{currencyFormatter(Number(text).toFixed(2))}</div>
      ),
    },
  ];

  if (!isProgress) {
    cols.push(
      {
        title: 'Previous Billings',
        dataIndex: 'previousBillings',
        width: 100,
        render: (text) => (
          <div>{currencyFormatter(Number(text).toFixed(2))}</div>
        ),
      },
      {
        title: 'Invoice',
        dataIndex: 'invoiceAmount',
        width: 140,
        render: (text, record) => (
          !readOnly && !record.hasSubItems
            ? (
              <ScheduleOfValuesInputField
                {...record}
                field="invoiceAmount"
                type="number"
                formatter={currencyFormatter}
                min={Number.MIN_SAFE_INTEGER}
                title="Invoice Amount"
              />
            ) : <div>{currencyFormatter(Number(text).toFixed(2))}</div>
        ),
      },
      {
        title: 'Notes',
        dataIndex: 'notesColumn',
        width: 125,
        render: (text, record) => {
          const isReadOnly = readOnly || isPastBill;
          const popoverContent = (
          /* eslint-disable-next-line */
            <div
              className="project-sov-notes"
              style={{
                border: isReadOnly ? 'none' : '1px solid #d9d9d9',
                cursor: isReadOnly ? 'default' : 'pointer',
              }}
              onClick={onNotesSelect && !isReadOnly ? () => onNotesSelect(record) : null}
            >
              {text}
            </div>
          );

          return (
            text ? (
              <Popover
                placement="left"
                content={<div style={{ width: 250 }}>{text}</div>}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              >
                {popoverContent}
              </Popover>
            ) : (
              <div>{popoverContent}</div>
            )
          );
        },
      },
    );
  }

  if (visibleColumnsSet.has('unitType')) {
    cols.push({
      title: 'Unit Type',
      dataIndex: 'unitType',
      width: 150,
      render: (text, record) => (
        !readOnly && !isScheduleOfValueLocked && !isPastBill && !record.hasSubItems
          ? (
            <ScheduleOfValuesInputField
              {...record}
              field="unitType"
              type="text"
              title="Unit Type"
              placeholder="Unit Type"
            />
          ) : <div>{text}</div>
      ),
    });
  }

  if (visibleColumnsSet.has('estimatedQuantity')) {
    cols.push({
      title: 'Est. Quantity',
      dataIndex: 'estimatedQuantity',
      width: 150,
      render: (text, record) => (
        !readOnly && !isScheduleOfValueLocked && !isPastBill && !record.hasSubItems
          ? (
            <ScheduleOfValuesInputField
              {...record}
              field="estimatedQuantity"
              type="number"
              title="Estimated Quantity"
              min={0}
            />
          ) : <div>{text}</div>
      ),
    });
  }

  if (visibleColumnsSet.has('previouslyBilledQuantity')) {
    cols.push({
      title: 'Previously Billed Qty',
      dataIndex: 'previouslyBilledQuantity',
      width: 150,
      render: (text) => <div>{text ?? 0}</div>,
    });
  }

  if (visibleColumnsSet.has('billableQuantity')) {
    cols.push({
      title: 'Billable Qty',
      dataIndex: 'billableQuantity',
      width: 150,
      render: (text, record) => (
        !readOnly && !isScheduleOfValueLocked && !isPastBill && !record.hasSubItems
          ? (
            <ScheduleOfValuesInputField
              {...record}
              field="billableQuantity"
              type="number"
              title="Billable Quantity"
              min={0}
            />
          ) : <div>{text}</div>
      ),
    });
  }

  if (visibleColumnsSet.has('completedQuantity')) {
    cols.push({
      title: 'Completed Quantity',
      dataIndex: 'completedQuantity',
      width: 160,
      render: (text, record) => (
        !readOnly && !isScheduleOfValueLocked && !isPastBill && !record.hasSubItems
          ? (
            <ScheduleOfValuesInputField
              {...record}
              field="completedQuantity"
              type="number"
              title="Completed Quantity"
              min={Number.MIN_SAFE_INTEGER}
            />
          ) : <div>{text}</div>
      ),
    });
  }

  if (visibleColumnsSet.has('quantityToDate')) {
    cols.push({
      title: 'Quantity To Date',
      dataIndex: 'quantityToDate',
      width: 190,
      render: (text) => <div>{text ?? 0}</div>,
    });
  }

  if (visibleColumnsSet.has('quantityRemaining')) {
    cols.push({
      title: 'Quantity Remaining',
      dataIndex: 'quantityRemaining',
      width: 160,
      render: (_text, record) => {
        const remaining = ScheduleOfValues.getQuantityRemaining(record);
        return (
          <div style={remaining < 0 ? { color: colors.ONTRACCR_RED } : undefined}>
            {remaining}
          </div>
        );
      },
    });
  }

  if (visibleColumnsSet.has('billingCode')) {
    cols.push({
      title: 'Billing Code',
      dataIndex: 'billingCode',
      width: 150,
      render: (text, record) => (
        !readOnly && !isScheduleOfValueLocked && !isPastBill && !record.hasSubItems
          ? (
            <ScheduleOfValuesInputField
              {...record}
              field="billingCode"
              type="text"
              title="Billing Code"
              placeholder="Billing Code"
            />
          ) : <div>{text}</div>
      ),
    });
  }

  if (!hideButtons) {
    if (onAddSubItem && !readOnly && !isScheduleOfValueLocked) {
      cols.push({
        title: 'Sub-Item',
        dataIndex: 'addSubItem',
        key: 'addSubItem',
        align: 'center',
        width: 20,
        render: (_, record) => (
          ScheduleOfValues.canRowHaveSubItems(record) ? (
            <BorderlessButton
              style={{ padding: 0, width: 20 }}
              iconNode={<PlusOutlined style={{ marginLeft: 0 }} />}
              onClick={() => onAddSubItem(record)}
            />
          ) : null
        ),
      });
    }

    cols.push({
      title: 'History',
      dataIndex: 'history',
      key: 'history',
      align: 'center',
      width: 10,
      render: (_, record) => (
        <BorderlessButton
          style={{ padding: 0, width: 10 }}
          iconNode={<HistoryOutlined style={{ marginLeft: 0 }} />}
          onClick={() => onHistory(record)}
        />
      ),
    });

    if (isSubContract) {
      cols.push({
        title: 'Info',
        dataIndex: 'info',
        key: 'info',
        align: 'center',
        width: 10,
        render: (_, record) => (
          <BorderlessButton
            style={{ padding: 0, width: 10 }}
            iconNode={<InfoCircleOutlined style={{ marginLeft: 0 }} />}
            onClick={() => onInfo(record)}
          />
        ),
      });
    }

    if (!readOnly && !isScheduleOfValueLocked) {
      cols.push({
        title: '',
        dataIndex: 'delete',
        key: 'delete',
        align: 'right',
        width: 10,
        render: (_, record) => (
          <BorderlessButton
            style={{ padding: 0, width: 10 }}
            iconNode={<DeleteOutlined style={{ marginLeft: 0, color: colors.ONTRACCR_RED }} />}
            onClick={() => onDelete(record)}
          />
        ),
      });
    }
  }

  return cols;
};

export const OPTIONAL_SOV_COLUMN_OPTIONS = [
  { label: 'Unit Type', value: 'unitType' },
  { label: 'Estimated Quantity', value: 'estimatedQuantity' },
  { label: 'Previously Billed Quantity', value: 'previouslyBilledQuantity' },
  { label: 'Billable Quantity', value: 'billableQuantity' },
  { label: 'Completed Quantity', value: 'completedQuantity' },
  { label: 'Quantity To Date', value: 'quantityToDate' },
  { label: 'Quantity Remaining', value: 'quantityRemaining' },
  { label: 'Billing Code', value: 'billingCode' },
];

export const ALL_OPTIONAL_SOV_COLUMNS = OPTIONAL_SOV_COLUMN_OPTIONS.map((o) => o.value);

export {
  getLiveSummaryColumns,
  getLogColumns,
  getScheduleOfValueColumns,
};
